














































































































import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import { DataTableHeader } from 'vuetify';
import ConfirmDialog from '@/components/dialog/ConfirmDialog.vue';
import { useGroups } from '@/store/modules/group';
import { useAllResources } from '@/store/modules/resources';
import ReviewStratificationJob from '@/views/stratify/ReviewStratificationJob.vue';
import soilApiService from '../../services/soilApiService';

interface StratificationJobsTableRow {
	fieldName: string;
	groupName: string;
	stratificationConfig: string;
	status: string;
	lastUpdated: string | null;
}

const formattedClosedReason = {
	APPROVED: 'Approved',
	REJECTED: 'Rejected',
	CANCELLED: 'Dismissed',
	TIMED_OUT: 'Timed Out',
	FAILED_TO_CREATE: 'Failed to Create',
};

const formattedExternalJobStatus = {
	SUCCESS: 'Success',
	FAILURE: 'Failed',
	PENDING: 'Pending',
};

const useStratificationJobs = () => {
	const isLoading = ref<boolean>(true);
	const stratificationJobs = ref<any[]>([]);
	const error = ref<any>(null);

	soilApiService
		.getStratificationJobs()
		.then((response) => {
			stratificationJobs.value = response.data;
		})
		.catch((err) => {
			error.value = err;
		})
		.finally(() => {
			isLoading.value = false;
		});

	return {
		isLoading,
		stratificationJobs,
		error,
	};
};

const getDisplayStatus = (stratificationJob: any) => {
	if (stratificationJob.isClosed) {
		return (formattedClosedReason as any)[stratificationJob.closedReason];
	} else {
		if (stratificationJob.externalJobStatus === 'SUCCESS') {
			return 'Awaiting Review';
		} else {
			return (formattedExternalJobStatus as any)[stratificationJob.externalJobStatus];
		}
	}
};

const getActions = (
	stratificationJob: any,
	cancelDialogOpenFor: any,
	stratificationJobUnderReview: any,
	approveDialogOpenFor: any
) => {
	if (stratificationJob.isClosed) {
		return [];
	}
	if (stratificationJob.externalJobStatus === 'SUCCESS') {
		return [
			{
				text: 'Review',
				handler: () => {
					stratificationJobUnderReview.value = stratificationJob;
				},
			},
			{
				text: 'Dismiss',
				handler: () => {
					cancelDialogOpenFor.value = stratificationJob.id;
				},
			},
			{
				text: 'Approve',
				handler: () => {
					approveDialogOpenFor.value = stratificationJob.id;
				},
			},
		];
	} else {
		return [
			{
				text: 'Dismiss',
				handler: () => {
					cancelDialogOpenFor.value = stratificationJob.id;
				},
			},
		];
	}
};

export default defineComponent({
	components: {
		ConfirmDialog,
		ReviewStratificationJob,
	},
	setup(_, { root }) {
		const search = ref<string>('');
		const page = ref<number>(1);
		const sortBy = ref<string[]>(['lastUpdated']);
		const sortDesc = ref<boolean[]>([true]);
		const { isLoading: jobsAreLoading, error: jobsHaveError, stratificationJobs } = useStratificationJobs();
		const { isLoading: areGroupsLoading, hasError: groupsHaveError } = useGroups(root.$store);
		const { isLoading: areResourcesLoading, isLoaded: areResourcesLoaded } = useAllResources(root.$store);
		const isLoading = computed<boolean>(
			() => jobsAreLoading.value || areGroupsLoading.value || areResourcesLoading.value
		);
		const error = computed<any>(() => jobsHaveError.value || groupsHaveError.value);
		const cancelDialogOpenFor = ref<string | null>(null);
		const cancelDialogIsLoading = ref<boolean>(false);
		const cancelDialogHasError = ref<boolean>(false);
		const isCancelDialogOpen = computed<boolean>(() => cancelDialogOpenFor.value !== null);
		const approveDialogOpenFor = ref<string | null>(null);
		const approveDialogIsLoading = ref<boolean>(false);
		const approveDialogHasError = ref<boolean>(false);
		const isApproveDialogOpen = computed<boolean>(() => approveDialogOpenFor.value !== null);
		const stratificationJobUnderReview = ref<any>(null);
		const isReviewDialogOpen = computed<boolean>(() => stratificationJobUnderReview.value !== null);
		const refreshJobs = () =>
			soilApiService
				.getStratificationJobs()
				.then((response) => {
					stratificationJobs.value = response.data;
				})
				.catch((err) => {
					jobsHaveError.value = err;
				})
				.finally(() => {
					jobsAreLoading.value = false;
				});

		const headers: DataTableHeader[] = [
			{ text: 'Field Name', value: 'fieldName' },
			{ text: 'Group', value: 'groupName' },
			{ text: 'Configuration', value: 'stratificationConfigDialog', sortable: false },
			{ text: 'Status', value: 'status' },
			{ text: 'Last Updated', value: 'lastUpdated' },
			{ text: 'Auto Approve', value: 'autoApprove' },
			{ text: 'Error', value: 'error', sortable: false },
			{ text: '', value: 'actions', sortable: false },
		];

		const tableData = computed<StratificationJobsTableRow[]>(() =>
			areResourcesLoaded.value
				? stratificationJobs.value.map((stratificationJob) => {
						const field = root.$store.getters['resources/getFieldByAreaId'](stratificationJob.areaId);

						return {
							fieldName: field?.name,
							groupName: root.$store.getters['groups/getGroupNameById'](field.meta.groupId),
							stratificationConfig: JSON.stringify(JSON.parse(stratificationJob.stratificationConfig), null, 2),
							status: getDisplayStatus(stratificationJob),
							lastUpdated: stratificationJob.updatedAt,
							error: stratificationJob.error,
							actions: getActions(
								stratificationJob,
								cancelDialogOpenFor,
								stratificationJobUnderReview,
								approveDialogOpenFor
							),
							isStratificationConfigDialogOpen: ref<boolean>(false),
							isErrorOpen: ref<boolean>(false),
							autoApprove: stratificationJob.autoApprove,
						};
				  })
				: []
		);

		const clearCancelDialogError = () => {
			cancelDialogHasError.value = false;
			cancelDialogOpenFor.value = null;
		};

		const handleConfirmCancel = async () => {
			try {
				cancelDialogIsLoading.value = true;
				await soilApiService.closeStratificationJob(cancelDialogOpenFor.value as string, 'CANCELLED');
				cancelDialogOpenFor.value = null;
				refreshJobs();
			} catch (error) {
				cancelDialogHasError.value = true;
			} finally {
				cancelDialogIsLoading.value = false;
			}
		};

		const clearApproveDialogError = () => {
			approveDialogHasError.value = false;
			approveDialogOpenFor.value = null;
		};

		const handleConfirmApprove = async () => {
			try {
				approveDialogIsLoading.value = true;
				await soilApiService.closeStratificationJob(approveDialogOpenFor.value as string, 'APPROVED');
				approveDialogOpenFor.value = null;
				refreshJobs();
			} catch (error) {
				approveDialogHasError.value = true;
			} finally {
				approveDialogIsLoading.value = false;
			}
		};

		const handleReviewSubmitted = () => {
			stratificationJobUnderReview.value = null;
			refreshJobs();
			root.$store.dispatch('resources/getAllResources');
		};

		const handleReviewCancel = () => {
			stratificationJobUnderReview.value = null;
		};

		watch(search, () => (page.value = 1));

		return {
			search,
			page,
			sortBy,
			sortDesc,
			headers,
			tableData,
			error,
			isLoading,
			cancelDialogOpenFor,
			isCancelDialogOpen,
			cancelDialogIsLoading,
			cancelDialogHasError,
			clearCancelDialogError,
			handleConfirmCancel,
			approveDialogOpenFor,
			isApproveDialogOpen,
			approveDialogIsLoading,
			approveDialogHasError,
			clearApproveDialogError,
			handleConfirmApprove,
			stratificationJobs,
			stratificationJobUnderReview,
			isReviewDialogOpen,
			handleReviewSubmitted,
			handleReviewCancel,
		};
	},
});
